/**
 * LifecycleMap
 *
 * Isolated Mapbox GL instance for the Fire Lifecycle Visualizer.
 * NOT connected to window.map — entirely self-contained.
 *
 * Displays one polygon per day from the lifecycle timeline:
 * - Orange fill/outline for GPS-uploaded perimeters
 * - Red fill/outline for hotspot-derived convex hulls
 * - Fire origin point (red dot)
 *
 * Fits map to the selected day's polygon bbox.
 * Full cleanup on unmount — no memory/event leaks.
 */
import React, { useEffect, useRef } from 'react';
import type { LifecycleFrame } from '../../../../hooks/useLifecycleData';
import mapboxgl from 'mapbox-gl';
import { getMapboxToken } from '../../../../services/configService';
interface LifecycleMapProps {
  frames: LifecycleFrame[];
  selectedDate: string;
  origin: [number, number] | null;
}

const SOURCE_POLYGON = 'lifecycle-polygon';
const SOURCE_ORIGIN = 'lifecycle-origin';

const LAYER_POLYGON_FILL = 'lifecycle-polygon-fill';
const LAYER_POLYGON_OUTLINE = 'lifecycle-polygon-outline';
const LAYER_ORIGIN = 'lifecycle-origin-layer';

const COLOR_GPS = '#ff6600';      // orange
const COLOR_HOTSPOT = '#cc0000';  // red

function buildPolygonFeature(frame: LifecycleFrame | undefined): GeoJSON.FeatureCollection {
  if (!frame?.polygon) return { type: 'FeatureCollection', features: [] };

  return {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: {
        source: frame.source,
        date: frame.date,
        areaHa: frame.areaHa ?? null,
      },
      geometry: frame.polygon,
    }],
  };
}

function getColor(frame: LifecycleFrame | undefined): string {
  if (!frame) return COLOR_GPS;
  return frame.source === 'gps' ? COLOR_GPS : COLOR_HOTSPOT;
}

function bboxFromMultiPolygon(mp: GeoJSON.MultiPolygon): [number, number, number, number] | null {
  let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity;
  for (const polygon of mp.coordinates) {
    for (const ring of polygon) {
      for (const [lon, lat] of ring) {
        if (lon < minLon) minLon = lon;
        if (lon > maxLon) maxLon = lon;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
      }
    }
  }
  if (!isFinite(minLon)) return null;
  return [minLon, minLat, maxLon, maxLat];
}

export const LifecycleMap: React.FC<LifecycleMapProps> = ({
  frames,
  selectedDate,
  origin,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const initializedRef = useRef(false);

  const currentFrame = frames.find(f => f.date === selectedDate);

  // Initialize map once
  useEffect(() => {
    if (!containerRef.current || initializedRef.current) return;
    initializedRef.current = true;

    // const token = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN as string;

    const token = getMapboxToken() as string;
    mapboxgl.accessToken = token;

    const center: [number, number] = origin ?? [-114.0, 64.0];

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/satellite-streets-v12',
      center,
      zoom: origin ? 9 : 5,
      attributionControl: false,
    });

    mapRef.current = map;

    map.on('load', () => {
      // Origin point
      map.addSource(SOURCE_ORIGIN, {
        type: 'geojson',
        data: origin
          ? { type: 'FeatureCollection', features: [{ type: 'Feature', properties: { label: 'Fire Origin' }, geometry: { type: 'Point', coordinates: origin } }] }
          : { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: LAYER_ORIGIN,
        type: 'circle',
        source: SOURCE_ORIGIN,
        paint: {
          'circle-color': '#ff0000',
          'circle-radius': 8,
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 2,
        },
      });

      // Polygon source + layers
      const initialColor = getColor(currentFrame);
      map.addSource(SOURCE_POLYGON, {
        type: 'geojson',
        data: buildPolygonFeature(currentFrame),
      });

      map.addLayer({
        id: LAYER_POLYGON_FILL,
        type: 'fill',
        source: SOURCE_POLYGON,
        paint: {
          'fill-color': initialColor,
          'fill-opacity': 0.35,
        },
      });

      map.addLayer({
        id: LAYER_POLYGON_OUTLINE,
        type: 'line',
        source: SOURCE_POLYGON,
        paint: {
          'line-color': initialColor,
          'line-width': 2,
        },
      });

      // Fit to first frame with a polygon, or all frames
      const firstWithPolygon = frames.find(f => f.polygon);
      if (firstWithPolygon?.polygon) {
        const allBbox = getAllFramesBbox(frames);
        if (allBbox) {
          map.fitBounds([[allBbox[0], allBbox[1]], [allBbox[2], allBbox[3]]], { padding: 40 });
        }
      }
    });

    return () => {
      map.remove();
      mapRef.current = null;
      initializedRef.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update polygon data + colors when selectedDate changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;

    const frame = frames.find(f => f.date === selectedDate);
    const color = getColor(frame);

    const src = map.getSource(SOURCE_POLYGON) as mapboxgl.GeoJSONSource | undefined;
    if (src) {
      src.setData(buildPolygonFeature(frame));
    }

    map.setPaintProperty(LAYER_POLYGON_FILL, 'fill-color', color);
    map.setPaintProperty(LAYER_POLYGON_OUTLINE, 'line-color', color);
  }, [selectedDate, frames]);

  return (
    <div
      ref={containerRef}
      className="lifecycle-map"
      style={{ width: '100%', height: '400px' }}
      aria-label="Fire lifecycle map"
    />
  );
};

/** Compute a bbox that contains all frames with polygons */
function getAllFramesBbox(frames: LifecycleFrame[]): [number, number, number, number] | null {
  let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity;
  for (const f of frames) {
    if (!f.polygon) continue;
    const b = bboxFromMultiPolygon(f.polygon);
    if (b) {
      if (b[0] < minLon) minLon = b[0];
      if (b[1] < minLat) minLat = b[1];
      if (b[2] > maxLon) maxLon = b[2];
      if (b[3] > maxLat) maxLat = b[3];
    }
  }
  if (!isFinite(minLon)) return null;
  return [minLon, minLat, maxLon, maxLat];
}
